/**
 * Organizations API Client
 * Talks to the backend endpoints shipped in Task 3.1.
 */

import axios from '@/config/axios';

// Bounds for organizations.audit_retention_years — mirror the backend
// UpdateOrganizationDto @Min/@Max so the form spinner can't offer values the
// API will reject (CORA-310).
export const AUDIT_RETENTION_YEARS_MIN = 1;
export const AUDIT_RETENTION_YEARS_MAX = 30;

export interface OrganizationDto {
  id: string;
  name: string;
  address: string;
  city: string;
  country: string;
  /** ISO 4217 alpha-3. One org = one country = one currency. */
  currencyCode: string;
  currencySymbol: string;
  /** Org-wide VAT rate applied to consultation invoice lines. Migration 20260519000005. */
  consultationVatRateId: string | null;
  /** Audit-log rows older than this many years are purged nightly (CORA-310). Backend defaults to 5. */
  auditRetentionYears: number;
  webUrl: string | null;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListOrganizationsParams {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  search?: string;
}

export interface PaginatedOrganizations {
  items: OrganizationDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateOrganizationPayload {
  name: string;
  address: string;
  city: string;
  country: string;
  currencyCode: string;
  currencySymbol: string;
  consultationVatRateId?: string | null;
  webUrl?: string | null;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone?: string | null;
}

export interface UpdateOrganizationPayload {
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  currencyCode?: string;
  currencySymbol?: string;
  consultationVatRateId?: string | null;
  auditRetentionYears?: number;
  webUrl?: string | null;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string | null;
  isActive?: boolean;
}

export const organizationsApi = {
  async list(params?: ListOrganizationsParams): Promise<PaginatedOrganizations> {
    const response = await axios.get<PaginatedOrganizations>('/organizations', { params });
    return response.data;
  },

  async getById(id: string): Promise<OrganizationDto> {
    const response = await axios.get<OrganizationDto>(`/organizations/${id}`);
    return response.data;
  },

  /**
   * Return the caller's own organization (any authenticated non-super-admin
   * user). Used by `organizationContext` to hydrate currency on login. The
   * backend returns 404 for super-admins — they have no own org.
   */
  async getMine(): Promise<OrganizationDto> {
    const response = await axios.get<OrganizationDto>('/organizations/me');
    return response.data;
  },

  async create(payload: CreateOrganizationPayload): Promise<OrganizationDto> {
    const response = await axios.post<OrganizationDto>('/organizations', payload);
    return response.data;
  },

  async update(id: string, payload: UpdateOrganizationPayload): Promise<OrganizationDto> {
    const response = await axios.patch<OrganizationDto>(`/organizations/${id}`, payload);
    return response.data;
  },
};
